import { useState, useEffect, useCallback, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { GST_PLACE_OF_SUPPLY_RULES as PLACE_OF_SUPPLY_RULES } from '../../../../shared/contracts/backend-enums.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';

/** GST rules are stored as date-effective system parameters under category GST. */
const GST_RULE_CODES = [
  'GST_PLACE_OF_SUPPLY_RULE',
  'GST_REVERSE_CHARGE_DEFAULT',
  'GST_ROUNDING_MODE',
] as const;

type Tab = 'rates' | 'rules' | 'stateCodes';

interface GstRate {
  readonly taxId: string;
  readonly taxCode: string;
  readonly taxName: string;
  readonly ratePercentage: number;
  readonly hsnSacCode: string | null;
  readonly applicableState: string | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
}

interface GstRule {
  readonly parameterId: string;
  readonly parameterCode: string;
  readonly parameterValue: string;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
}

interface GstStateCode {
  readonly gstStateCodeId: string;
  readonly gstStateCode: string;
  readonly stateName: string | null;
  readonly stateCode: string | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
}

/** L17.09 — GST Configuration (`/admin/master-config/gst`).
 *  Every GST rule is date-effective so it can change over time without a code
 *  change. Backed by the existing t211 / t214 / t615 tables. */
export function M17GstConfigPage(): ReactNode {
  const { t } = useTranslation();
  // effective_from_date / effective_to_date on t615_gst_state_code are `date`
  // columns — stored business days, so they take `day` and not `at`.
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const [tab, setTab] = useState<Tab>('rates');
  const [rates, setRates] = useState<readonly GstRate[]>([]);
  const [rules, setRules] = useState<readonly GstRule[]>([]);
  const [stateCodes, setStateCodes] = useState<readonly GstStateCode[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, u, s] = await Promise.all([
        apiRequest<{ data: GstRate[] }>('/v1/m17/gst-config/rates', { token }),
        apiRequest<{ data: GstRule[] }>('/v1/m17/gst-config/rules', { token }),
        apiRequest<{ data: GstStateCode[] }>('/v1/m17/gst-config/state-codes', { token }),
      ]);
      setRates(r.data ?? []);
      setRules(u.data ?? []);
      setStateCodes(s.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  // ── Rate form ──────────────────────────────────────────────────────────────
  const [rateCode, setRateCode] = useState('');
  const [rateName, setRateName] = useState('');
  const [ratePct, setRatePct] = useState('');
  const [hsnSac, setHsnSac] = useState('');
  const [applicableState, setApplicableState] = useState('');
  const [rateFrom, setRateFrom] = useState('');
  const [rateTo, setRateTo] = useState('');

  async function submitRate(e: FormEvent) {
    e.preventDefault();
    setError(null); setNotice(null);
    if (!rateCode || !rateName) { setError(t('m17.gst.errRateFields')); return; }
    const pct = Number(ratePct);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) { setError(t('m17.gst.errRatePct')); return; }
    if (!rateFrom) { setError(t('m17.gst.errFrom')); return; }
    if (rateTo && rateTo <= rateFrom) { setError(t('m17.gst.errDateOrder')); return; }
    try {
      await apiRequest('/v1/m17/engine/taxes', {
        method: 'POST', token,
        body: {
          taxCode: rateCode, taxName: rateName, taxType: 'GST',
          ratePercentage: pct,
          ...(hsnSac ? { hsnSacCode: hsnSac } : {}),
          ...(applicableState ? { applicableState } : {}),
          effectiveFromDate: rateFrom,
          ...(rateTo ? { effectiveToDate: rateTo } : {}),
        },
      });
      setNotice(t('m17.gst.saved'));
      setRateCode(''); setRateName(''); setRatePct(''); setHsnSac(''); setApplicableState(''); setRateFrom(''); setRateTo('');
      await load();
    } catch (err) { setError((err as Error).message); }
  }

  // ── Rule form ──────────────────────────────────────────────────────────────
  const [ruleCode, setRuleCode] = useState<string>(GST_RULE_CODES[0]);
  const [ruleValue, setRuleValue] = useState('');
  const [ruleFrom, setRuleFrom] = useState('');
  const [ruleTo, setRuleTo] = useState('');

  async function submitRule(e: FormEvent) {
    e.preventDefault();
    setError(null); setNotice(null);
    if (!ruleValue) { setError(t('m17.gst.errRuleValue')); return; }
    if (!ruleFrom) { setError(t('m17.gst.errFrom')); return; }
    if (ruleTo && ruleTo <= ruleFrom) { setError(t('m17.gst.errDateOrder')); return; }
    try {
      await apiRequest('/v1/m17/engine/parameters', {
        method: 'POST', token,
        body: {
          parameterCode: ruleCode,
          parameterName: ruleCode,
          parameterValue: ruleValue,
          valueType: 'STRING',
          category: 'GST',
          effectiveFromDate: ruleFrom,
          ...(ruleTo ? { effectiveToDate: ruleTo } : {}),
        },
      });
      setNotice(t('m17.gst.saved'));
      setRuleValue(''); setRuleFrom(''); setRuleTo('');
      await load();
    } catch (err) { setError((err as Error).message); }
  }

  const posRule = rules.find((r) => r.parameterCode === 'GST_PLACE_OF_SUPPLY_RULE');

  return (
    <main aria-label="gst-config">
      <h1>{t('m17.gst.title')}</h1>

      {!posRule ? <p role="status">{t('m17.gst.warnNoPlaceOfSupply')}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      <label htmlFor="gst-tab">{t('m17.gst.section')}</label>
      <select id="gst-tab" value={tab} onChange={(e) => setTab(e.target.value as Tab)}>
        <option value="rates">{t('m17.gst.tabRates')}</option>
        <option value="rules">{t('m17.gst.tabRules')}</option>
        <option value="stateCodes">{t('m17.gst.tabStateCodes')}</option>
      </select>

      {tab === 'rates' ? (
        <section aria-label="gst-rates">
          <h2>{t('m17.gst.tabRates')}</h2>
          <form onSubmit={submitRate} aria-label="gst-rate-form" noValidate onSubmitCapture={validateDateSubmission}>
            <label htmlFor="gst-rate-code">{t('m17.gst.code')}</label>
            <input id="gst-rate-code" value={rateCode} onChange={(e) => setRateCode(e.target.value)} />
            <label htmlFor="gst-rate-name">{t('m17.gst.name')}</label>
            <input id="gst-rate-name" value={rateName} onChange={(e) => setRateName(e.target.value)} />
            <label htmlFor="gst-rate-pct">{t('m17.gst.ratePct')}</label>
            <input id="gst-rate-pct" value={ratePct} onChange={(e) => setRatePct(e.target.value)} />
            <label htmlFor="gst-rate-hsn">{t('m17.gst.hsnSac')}</label>
            <input id="gst-rate-hsn" value={hsnSac} onChange={(e) => setHsnSac(e.target.value)} />
            <label htmlFor="gst-rate-state">{t('m17.gst.applicableState')}</label>
            <input id="gst-rate-state" value={applicableState} onChange={(e) => setApplicableState(e.target.value)} />
            <label htmlFor="gst-rate-from">{t('m17.gst.effectiveFrom')}</label>
            <DateInput id="gst-rate-from" type="date" value={rateFrom} onValueChange={(nextValue) => setRateFrom(nextValue)} />
            <label htmlFor="gst-rate-to">{t('m17.gst.effectiveTo')}</label>
            <DateInput id="gst-rate-to" type="date" value={rateTo} onValueChange={(nextValue) => setRateTo(nextValue)} />
            <button type="submit">{t('m17.gst.addRate')}</button>
          </form>
          <table aria-label="gst-rates-table">
            <thead>
              <tr>
                <th>{t('m17.gst.code')}</th><th>{t('m17.gst.name')}</th><th>{t('m17.gst.ratePct')}</th>
                <th>{t('m17.gst.hsnSac')}</th><th>{t('m17.gst.applicableState')}</th>
                <th>{t('m17.gst.effectiveFrom')}</th><th>{t('m17.gst.effectiveTo')}</th>
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 ? <tr><td colSpan={7}>{t('m17.gst.noRates')}</td></tr> : rates.map((r) => (
                <tr key={r.taxId}>
                  <td>{r.taxCode}</td><td>{r.taxName}</td><td>{r.ratePercentage}</td>
                  <td>{r.hsnSacCode ?? '-'}</td><td>{r.applicableState ?? t('m17.gst.allStates')}</td>
                  <td>{day(r.effectiveFromDate)}</td><td>{day(r.effectiveToDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {tab === 'rules' ? (
        <section aria-label="gst-rules">
          <h2>{t('m17.gst.tabRules')}</h2>
          <form onSubmit={submitRule} aria-label="gst-rule-form" noValidate onSubmitCapture={validateDateSubmission}>
            <label htmlFor="gst-rule-code">{t('m17.gst.rule')}</label>
            <select id="gst-rule-code" value={ruleCode} onChange={(e) => { setRuleCode(e.target.value); setRuleValue(''); }}>
              {GST_RULE_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label htmlFor="gst-rule-value">{t('m17.gst.value')}</label>
            {ruleCode === 'GST_PLACE_OF_SUPPLY_RULE' ? (
              <select id="gst-rule-value" value={ruleValue} onChange={(e) => setRuleValue(e.target.value)}>
                <option value="">{t('m17.gst.selectValue')}</option>
                {PLACE_OF_SUPPLY_RULES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <input id="gst-rule-value" value={ruleValue} onChange={(e) => setRuleValue(e.target.value)} />
            )}
            <label htmlFor="gst-rule-from">{t('m17.gst.effectiveFrom')}</label>
            <DateInput id="gst-rule-from" type="date" value={ruleFrom} onValueChange={(nextValue) => setRuleFrom(nextValue)} />
            <label htmlFor="gst-rule-to">{t('m17.gst.effectiveTo')}</label>
            <DateInput id="gst-rule-to" type="date" value={ruleTo} onValueChange={(nextValue) => setRuleTo(nextValue)} />
            <button type="submit">{t('m17.gst.addRule')}</button>
          </form>
          <table aria-label="gst-rules-table">
            <thead>
              <tr>
                <th>{t('m17.gst.rule')}</th><th>{t('m17.gst.value')}</th>
                <th>{t('m17.gst.effectiveFrom')}</th><th>{t('m17.gst.effectiveTo')}</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? <tr><td colSpan={4}>{t('m17.gst.noRules')}</td></tr> : rules.map((r) => (
                <tr key={r.parameterId}>
                  <td>{r.parameterCode}</td><td>{r.parameterValue}</td>
                  <td>{day(r.effectiveFromDate)}</td><td>{day(r.effectiveToDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {tab === 'stateCodes' ? (
        <section aria-label="gst-state-codes">
          <h2>{t('m17.gst.tabStateCodes')}</h2>
          <table aria-label="gst-state-codes-table">
            <thead>
              <tr>
                <th>{t('m17.gst.gstCode')}</th><th>{t('m17.gst.state')}</th>
                <th>{t('m17.gst.effectiveFrom')}</th><th>{t('m17.gst.effectiveTo')}</th>
              </tr>
            </thead>
            <tbody>
              {stateCodes.length === 0 ? <tr><td colSpan={4}>{t('m17.gst.noStateCodes')}</td></tr> : stateCodes.map((s) => (
                <tr key={s.gstStateCodeId}>
                  <td>{s.gstStateCode}</td><td>{s.stateName ?? s.stateCode ?? '-'}</td>
                  <td>{day(s.effectiveFromDate)}</td><td>{day(s.effectiveToDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </main>
  );
}
